import { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Mail, Lock, User, Phone, PhoneCall } from 'lucide-react';
import { googleLoginApi, signupApi } from './userapis';
import { sendOtp, verifyOtp } from '../instrutorProfile/profileAPIs';
import toast, { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';

const SignupPage = () => {
  const isExistingAccountMessage = (message?: string) => {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return (
      normalized.includes('already') ||
      normalized.includes('exist') ||
      normalized.includes('registered')
    );
  };

  // OTP state
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
    const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [resendTimer, setResendTimer] = useState(0);
    const [phoneForOtp, setPhoneForOtp] = useState('');
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Form state
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
        phone: '',
        // dateOfBirth: '',
        password: '',
        confirmPassword: '',
        agreeToTerms: false,
        subscribeNewsletter: false
    });

    const googleAPI = process.env.REACT_APP_API_GOOGLE_OAUTH;
    const navigate = useNavigate();

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target;
    const name = target.name;
    const value = target.value;
    const type = target.type;
    const checked = (target as HTMLInputElement).checked;

    setFormData(prev => {
      return {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
    });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.agreeToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    if (!otpVerified) {
      toast.error('Please verify your phone number');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      const res = await signupApi(formData);

      if (res.token) {
        sessionStorage.setItem('userAccessToken', res.token);
        toast.success('Account created successfully!');
        navigate("/profile", { replace: true });
      } else if (res.message) {
        if (isExistingAccountMessage(res.message)) {
          toast.error(res.message || 'Account already exists. Please login.');
          navigate("/login", {
            replace: true,
            state: { phone: phoneForOtp || formData.phone }
          });
        } else {
          toast.error(res.message);
          // Reset phone verification if signup fails
          setTimeout(() => {
            resetPhoneVerification();
          }, 1500);
        }
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again later.');
      // Reset phone verification on error
      setTimeout(() => {
        resetPhoneVerification();
      }, 1500);
    }
  };

  // Resend timer effect
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Reset phone verification to allow entering a new number
  const resetPhoneVerification = () => {
    setFormData(prev => ({ ...prev, phone: '' }));
    setOtp(['', '', '', '', '', '']);
    setOtpSent(false);
    setOtpVerified(false);
    setOtpError('');
    setPhoneForOtp('');
    setResendTimer(0);
  };

  // Send OTP
  const handleSendOtp = async () => {
    const phone = formData.phone.replace(/\D/g, '');

    if (!/^\d{10}$/.test(phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setIsSendingOTP(true);
    setOtpError('');

    try {
      const res = await sendOtp(phone);

      if (res.success) {
        toast.success(res.message || 'OTP sent successfully!');
        setOtpSent(true);
        setPhoneForOtp(phone);
        setResendTimer(60);
        setOtp(['', '', '', '', '', '']);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        if (isExistingAccountMessage(res.message)) {
          toast.error(res.message || 'Account already exists. Please login.');
          navigate("/login", {
            replace: true,
            state: { phone }
          });
        } else {
          toast.error(res.message || 'Failed to send OTP');
        }
      }
    } catch (error: any) {
      console.error("[Signup] Error sending OTP:", error);
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setIsSendingOTP(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async (providedOtp?: string) => {
    const otpValue = providedOtp || otp.join('');

    if (otpValue.length !== 6) {
      toast.error('Please enter complete 6-digit OTP');
      return;
    }

    setIsVerifyingOTP(true);

    try {
      const res = await verifyOtp(phoneForOtp, otpValue);

      if (res.success && res.verified) {
        toast.success('Phone number verified successfully!');
        setOtpVerified(true);
        setOtpError('');
      } else {
        setOtpError(res.message || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch {
      setOtpError('Verification failed. Please try again.');
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  // Handle OTP input changes
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-verify if all digits are entered
    if (newOtp.every(digit => digit !== '')) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  // Handle OTP input keydown events
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Handle Google login
  const handleGoogleLogin = async (response: any) => {
    if (response.credential) {
      try {
        const res = await googleLoginApi(response.credential);
        if (res) {
          sessionStorage.setItem('userAccessToken', res.token);
          toast.success('Login successful!');
          navigate("/profile", { replace: true });
        }
      } catch {
        toast.error('Google login failed. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50">
      <Toaster />

      {/* Main Content */}
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">

          {/* Logo and Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-4">
              <span className="text-white font-bold text-2xl">M</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your Account</h2>
            <p className="text-gray-600">Join our wellness community today</p>
          </div>

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="First name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Phone and OTP Section */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="grid grid-cols-1 gap-3">
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                    placeholder="Enter 10-digit phone number"
                  />
                </div>

                {/* OTP Section */}
                <div className="space-y-3">
                  {!otpVerified ? (
                    !otpSent ? (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isSendingOTP}
                        className={`w-full flex justify-center items-center gap-2 py-3 rounded-lg transition-all ${isSendingOTP
                            ? 'bg-yellow-400 cursor-not-allowed'
                            : 'bg-yellow-500 hover:bg-yellow-600'
                          } text-white`}
                      >
                        <PhoneCall size={16} />
                        {isSendingOTP ? 'Sending OTP...' : 'Send OTP'}
                      </button>
                    ) : (
                      <>
                        <div className="flex justify-center gap-1.5 sm:gap-2">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              ref={(el) => (otpRefs.current[index] = el)}
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              className="w-9 h-10 sm:w-11 sm:h-11 text-center border border-gray-300 rounded-lg text-base sm:text-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                              type="text"
                              inputMode="numeric"
                            />
                          ))}
                        </div>

                        {otpError && (
                          <p className="text-center text-red-500 text-sm mt-1">{otpError}</p>
                        )}

                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleVerifyOtp()}
                            disabled={isVerifyingOTP}
                            className={`flex-1 py-2.5 rounded-lg transition-all ${isVerifyingOTP
                                ? 'bg-teal-400 cursor-not-allowed'
                                : 'bg-teal-500 hover:bg-teal-600'
                              } text-white`}
                          >
                            {isVerifyingOTP ? 'Verifying...' : 'Verify OTP'}
                          </button>

                          <button
                            type="button"
                            onClick={resetPhoneVerification}
                            className="px-4 py-2.5 text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors border border-teal-600 rounded-lg hover:bg-teal-50"
                          >
                            Change Number
                          </button>
                        </div>

                        {resendTimer > 0 ? (
                          <p className="text-center text-sm text-gray-500">
                            Resend OTP in {resendTimer}s
                          </p>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendOtp}
                            className="text-teal-600 hover:text-teal-700 text-sm mx-auto block transition-colors"
                          >
                            Resend OTP
                          </button>
                        )}
                      </>
                    )
                  ) : (
                    <div className="flex items-center justify-center gap-2 py-2">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-green-600 font-medium">Phone Verified</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Date of Birth */}
            {/* <div>
              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="dateOfBirth"
                  name="dateOfBirth"
                  type="date"
                  required
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                />
              </div>
            </div> */}

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  id="agreeToTerms"
                  name="agreeToTerms"
                  type="checkbox"
                  required
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded transition-all"
                />
                <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-700">
                  I agree to the{' '}
                  <a href="#" className="text-teal-600 hover:text-teal-700 underline transition-colors">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-teal-600 hover:text-teal-700 underline transition-colors">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="subscribeNewsletter"
                  name="subscribeNewsletter"
                  type="checkbox"
                  checked={formData.subscribeNewsletter}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded transition-all"
                />
                <label htmlFor="subscribeNewsletter" className="ml-2 block text-sm text-gray-700">
                  Subscribe to our wellness newsletter
                </label>
              </div>
            </div>

            {/* Sign Up Button */}
            <button
              type="submit"
              disabled={!formData.agreeToTerms || !otpVerified}
              className={`w-full py-3 rounded-lg transition-all ${!formData.agreeToTerms || !otpVerified
                  ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                  : 'bg-teal-500 hover:bg-teal-600 text-white'
                }`}
            >
              Create Account
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Social Login */}
            <div className="flex justify-center">
              <GoogleOAuthProvider clientId={googleAPI || ""}>
                <GoogleLogin
                  onSuccess={handleGoogleLogin}
                  onError={() => toast.error('Google login failed')}
                  width={300}
                  theme="filled_blue"
                  shape="pill"
                />
              </GoogleOAuthProvider>
            </div>

            {/* Login Link */}
            <div className="text-center mt-6">
              <span className="text-gray-600">Already have an account? </span>
              <a
                href="/login"
                className="text-teal-600 hover:text-teal-700 font-medium transition-colors"
              >
                Sign in here
              </a>
            </div>
          </form>
        </div>
      </div>

      {/* Footer Security Info */}
      <div className="text-center py-6 text-sm text-gray-500 bg-white/50 border-t border-gray-100">
        <div className="flex items-center justify-center space-x-6 mb-2">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            SSL Encrypted
          </div>
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            Fast & Secure
          </div>
        </div>
        <p className="mt-2">
          By signing up, you agree to our{' '}
          <a href="#" className="text-teal-600 hover:underline transition-colors">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="text-teal-600 hover:underline transition-colors">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
